import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { IKBA } from '../../../types/KBA';
import { stateOptions } from '../../constants';
import { normalizePhoneInput } from '../../utils/helpers';

interface IProps {
  questions: Record<string, any>;
  cancel: () => void;
  goNext: (answer: Record<string, string>) => void;
}

export default function KBAForm(props: IProps) {
  const { questions, cancel, goNext } = props;

  // this conditionally creates a yup object
  const shapeWithDynamicFields = Object.keys(questions).reduce(
    (obj: any, key) => {
      if (key.includes('KBA')) {
        obj[key] = Yup.mixed().required();
      }
      return obj;
    },
    {}
  );

  // this conditionally creates initial values object
  const initialValuesDynamicFields = Object.keys(questions).reduce(
    (obj: any, key) => {
      if (key.includes('KBA')) {
        obj[key] = '';
      }
      return obj;
    },
    {}
  );

  const schema = Yup.object().shape(shapeWithDynamicFields);

  const renderForm = (formikProps: FormikProps<Record<string, string>>) => {
    const {
      errors,
      dirty,
      isValid,
      isSubmitting,
      getFieldProps,
      handleSubmit,
      setFieldValue,
    } = formikProps;

    const constructInput = (key: string, values: IKBA) => {
      switch (values.AnswerFormatType) {
        case 'SSN':
          return (
            <TextField
              fullWidth
              type='password'
              size='small'
              {...getFieldProps(key)}
              error={key in errors}
            />
          );
        case 'Phone':
          return (
            <TextField
              error={key in errors}
              fullWidth
              size='small'
              inputProps={{ maxLength: 15 }}
              {...getFieldProps(key)}
              variant='outlined'
              onChange={(e) =>
                setFieldValue(
                  key,
                  normalizePhoneInput(e.target.value).trimStart()
                )
              }
            />
          );
        case 'State':
          return (
            <Select
              variant='outlined'
              size='small'
              fullWidth
              {...getFieldProps(key)}>
              {stateOptions.length != 0 &&
                stateOptions.map((state, index) => (
                  <MenuItem key={index} value={state.Code}>
                    {state.Code || ''}
                  </MenuItem>
                ))}
            </Select>
          );
        case 'Numeric':
          return (
            <TextField
              variant='outlined'
              fullWidth
              type='number'
              size='small'
              {...getFieldProps(key)}
              onChange={(e) => setFieldValue(key, e.target.value.toString())}
              error={key in errors}
            />
          );
        case 'Date':
          return (
            <TextField
              variant='outlined'
              fullWidth
              type='date'
              size='small'
              {...getFieldProps(key)}
              error={key in errors}
            />
          );
        default:
          return (
            <TextField
              variant='outlined'
              fullWidth
              type='text'
              size='small'
              {...getFieldProps(key)}
              error={key in errors}
            />
          );
      }
    };

    return (
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} p={5}>
          {Object.entries(questions).map(
            ([key, value]) =>
              key.includes('KBA') && (
                <Box
                  key={key}
                  sx={{ display: 'flex', width: '100%', padding: '10px 0' }}>
                  <Grid item xs={12} md={5}>
                    <Typography sx={{ fontWeight: 'bold' }} mt={1}>
                      {JSON.parse(value).Question}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    {constructInput(key, JSON.parse(value))}
                  </Grid>
                </Box>
              )
          )}
          <Grid container justifyContent='space-between' pt={3}>
            <Button variant='outlined' onClick={cancel}>
              Cancel
            </Button>
            <Button
              disabled={!(isValid && dirty) || isSubmitting}
              type='submit'
              variant='contained'
              color='primary'>
              Next
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Formik
      enableReinitialize
      validateOnChange={true}
      validateOnBlur={true}
      initialValues={initialValuesDynamicFields}
      validationSchema={schema}
      onSubmit={(values) => {
        goNext(values);
      }}>
      {renderForm}
    </Formik>
  );
}
